import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button, sendToast } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';

// Import from the specific module, not the '../utils' barrel: the barrel pulls
// the whole app graph (create-project → api-services enums), which breaks a
// focused jsdom unit test of this hook.
import {
  hasAccessGrantedFlag,
  stripAccessGrantedParam,
} from '../utils/access-setup';
import { useManagerCtxSelector } from '../xstate/app.xstate';

const ACCESS_GRANTED_TOAST_ID = 'access-granted';

/**
 * PSTUDIO-4098. Shows the persistent "Access granted" toast when the user lands
 * back on Manager from the access-setup page with `?accessGranted=1` (an INTERNAL
 * redirect), then strips that one param so a reload does not re-show it.
 *
 * The one-shot `shownRef` survives a React 18 StrictMode replay — `main.tsx`
 * wraps the app unconditionally, so the effect runs twice on mount against the
 * same closure, and without the ref the toast would fire twice before the
 * flag-stripping navigation lands. (`toastId` dedups as a backstop.)
 *
 * Unlike the Pro Studio twin this does NOT defer the show: Manager's
 * `<ToastContainer>` is a child of `RouteDataProvider` and is therefore mounted
 * by the time this effect runs, and there is no `/`→home redirect dropping the
 * query, so a single effect suffices.
 *
 * ORDER IS LOAD-BEARING: call this hook AFTER the org-scoping redirect effect in
 * `RouteDataProvider`. That effect navigates to `/organization/:org/...` WITHOUT
 * the query; running first it would strip `accessGranted` from the URL before
 * this hook reads it. Declared later, this hook captures the flag (via the ref)
 * and its own strip-navigate lands last in the same commit.
 *
 * Reads the RAW `location.search`, not `useSearchParams().toString()`, so
 * `stripAccessGrantedParam` can keep the other params' encoding intact.
 */
export const useAccessGrantedToast = () => {
  const { t } = useTranslation();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const shownRef = useRef(false);

  const myAccount = useManagerCtxSelector((ctx) => ctx.routes.myAccount);
  // The toast outlives this render; read the latest route from a ref so the
  // button click is correct even if the value changed after the toast was shown.
  const myAccountRef = useRef(myAccount);
  myAccountRef.current = myAccount;

  useEffect(() => {
    if (!hasAccessGrantedFlag(search)) {
      return;
    }

    if (!shownRef.current) {
      shownRef.current = true;
      sendToast({
        type: 'info',
        // Neutral card: no success tint, no type icon (design).
        icon: false,
        // Size to content so the sentence reads on one line (design).
        className: 'c-toast--wide',
        toastId: ACCESS_GRANTED_TOAST_ID,
        // `false` is what makes it persistent: no auto-dismiss.
        autoClose: false,
        header:
          t('manager/common:accessGranted.header', {
            defaultValue: 'Access granted',
          }) ?? '',
        text:
          t('manager/common:accessGranted.body', {
            defaultValue:
              "You've been assigned the Pro Studio Admin role for this project.",
          }) ?? '',
        // `footer` renders the action UNDER the text, aligned to the end (design).
        footer: (
          <Button
            size="small"
            variant="secondary"
            onClick={() => navigate(myAccountRef.current)}
          >
            {t('manager/common:accessSetup.actions.myAccount', {
              defaultValue: 'My Account',
            })}
          </Button>
        ),
      });
    }

    navigate(`${pathname}${stripAccessGrantedParam(search)}${hash}`, {
      replace: true,
    });
  }, [search, pathname, hash, navigate, t]);
};
